import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthUser } from '../../models/auth/auth.models';

@Injectable({ providedIn: 'root' })
export class AuthService  {
  private readonly allowedRoles = new Set(['ADMIN', 'TENANT', 'OWNER']);
  private userSubject = new BehaviorSubject<AuthUser | null>(this.restore());
  readonly user$ = this.userSubject.asObservable();
  readonly isAuthenticated$ = this.user$.pipe(map(u => this.hasAllowedAccess(u)));

  constructor(private http: HttpClient) {  }

  async login(email: string, password: string): Promise<void> {
    const res = await firstValueFrom(this.http.post<any>(`${environment.apiBaseUrl}/login`, { email, password }));
    const token: string | undefined = res?.token || res?.accessToken || res?.data?.token;
    if (!token) throw new Error('Token ausente na resposta de login');
    if (!this.hasAllowedRole(this.extractRoles(res))) {
      this.logout();
      throw new Error('Acesso permitido apenas para perfis autorizados');
    }
    localStorage.setItem('authToken', token);

    try {
      const me = await firstValueFrom(this.http.get<any>(`${environment.apiBaseUrl}/my-self`));
      const user = this.mapUser(me, email, this.extractRoles(res));
      if (!this.hasAllowedAccess(user)) {
        this.logout();
        throw new Error('Acesso permitido apenas para perfis autorizados');
      }
      this.setUser(user);
    } catch (err) {
      this.logout();
      throw err;
    }
  }

  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    this.userSubject.next(null);
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  getCurrentUser(): AuthUser | null {
    return this.userSubject.value;
  }

  hasRole(role: string): boolean {
    return this.extractRoles(this.getCurrentUser()).includes(String(role).trim().toUpperCase());
  }

  async validateSession(): Promise<void> {
    const token = this.getToken();
    const user = this.restore();

    if (!token || !user) {
      this.logout();
      return;
    }

    try {
      const me = await firstValueFrom(this.http.get<any>(`${environment.apiBaseUrl}/my-self`));
      const nextUser = this.mapUser(me, user.email, user.roles);
      if (!this.hasAllowedAccess(nextUser)) {
        this.logout();
        return;
      }
      this.setUser(nextUser);
    } catch {
      this.logout();
    }
  }

  private restore(): AuthUser | null {
    if (!this.getToken()) return null;
    const raw = localStorage.getItem('authUser');
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as AuthUser;
      return this.hasAllowedAccess(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  forgotPassword(email: string) {
    return this.http.post(`${environment.apiBaseUrl}/no-auth/forgot`, { email });
  }

  resetPassword(payload: { code: string; password: string; confirmPassword: string; }) {
    return this.http.post(`${environment.apiBaseUrl}/no-auth/reset`, payload);
  }

  private setUser(user: AuthUser): void {
    localStorage.setItem('authUser', JSON.stringify(user));
    this.userSubject.next(user);
  }

  private mapUser(me: any, fallbackEmail = '', fallbackRoles: string[] = []): AuthUser {
    const roles = this.extractRoles(me, fallbackRoles);
    return {
      id: String(me?.id ?? me?.userId ?? ''),
      name: me?.name ?? me?.profile?.name ?? '',
      email: me?.email ?? fallbackEmail,
      roles
    };
  }

  private extractRoles(source: any, fallback: string[] = []): string[] {
    const rawRoles = Array.isArray(source?.roles)
      ? source.roles
      : source?.role
        ? [source.role]
        : fallback;

    return rawRoles
      .map((role: unknown) => String(role).trim().toUpperCase())
      .filter(Boolean);
  }

  private hasAllowedRole(roles: string[]): boolean {
    return roles.some(role => this.allowedRoles.has(role));
  }

  private hasAllowedAccess(user: AuthUser | null | undefined): boolean {
    return !!user?.id && this.hasAllowedRole(this.extractRoles(user));
  }
}
